import { Box, Typography, Grid, TextField } from "@mui/material";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { PaymentMethod } from "generated-graphql/graphql";
import { useEffect, useRef } from "react";
import { Control, UseFormWatch, UseFormSetValue } from "react-hook-form";

import { safeParseFloat } from "../lib/numberUtils";
import { PayeeFormInputs } from "../lib/types";

// Helper to format total amount (amount + transactionFee + lateFee)
function formatTotalAmount(
  amount: number | string | undefined,
  transactionFee: number | string | undefined,
  lateFee: number | string | undefined
): string {
  const amountNum = safeParseFloat(String(amount ?? 0)) ?? 0;
  const feeNum = safeParseFloat(String(transactionFee ?? 0)) ?? 0;
  const lateFeeNum = safeParseFloat(String(lateFee ?? 0)) ?? 0;
  const total = amountNum + feeNum + lateFeeNum;
  return total.toFixed(2);
}

interface PaymentSectionProps {
  control: Control<PayeeFormInputs>;
  isLob: boolean;
  notEditable: boolean;
  billingMetadataEditable?: boolean;
  setReportAmounts: () => void;
  watch: UseFormWatch<PayeeFormInputs>;
  setValue: UseFormSetValue<PayeeFormInputs>;
}

export function PaymentSection({
  control,
  isLob,
  notEditable,
  billingMetadataEditable = true,
  setReportAmounts,
  watch,
  setValue,
}: PaymentSectionProps) {
  const billable = watch("billable");
  const previousBillable = useRef<boolean | undefined>(undefined);

  const lateFee = watch("lateFee");
  const lateFeePresent = (safeParseFloat(String(lateFee ?? 0)) ?? 0) > 0;

  // Clear notes when both triggers go false (billable flips true AND no late fee)
  useEffect(() => {
    if (previousBillable.current === false && billable === true && !lateFeePresent) {
      setValue("comment", "");
    }
    previousBillable.current = billable;
  }, [billable, lateFeePresent, setValue]);

  // Determine if billing metadata should be disabled
  const billingDisabled = !billingMetadataEditable;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Payment
      </Typography>
      <Grid container columnSpacing={1} rowSpacing={0}>
        {/* First row: Memo and Invoice # */}
        <Grid size={6}>
          <FormTextField
            name="memo"
            label="Memo"
            maxLength={isLob ? 40 : undefined}
            control={control}
            disabled={notEditable}
          />
        </Grid>
        <Grid size={6}>
          <FormTextField
            name="invoiceNumber"
            label="Invoice #"
            control={control}
            disabled={notEditable}
          />
        </Grid>

        {/* Second row: Payment Method, Amount */}
        <Grid size={8}>
          <FormSelect
            name="paymentMethod"
            label="Payment Method"
            control={control}
            selectItems={[
              { value: PaymentMethod.Check, display: "Check" },
              {
                value: PaymentMethod.CreditCard,
                display: "Credit Card",
              },
              { value: PaymentMethod.Ach, display: "ACH" },
            ]}
            disabled={isLob || notEditable}
          />
        </Grid>
        <Grid size={4}>
          <FormTextField
            name="amount"
            label="Amount"
            control={control}
            onBlur={setReportAmounts}
            disabled={notEditable}
          />
        </Grid>

        {/* Third row: Transaction Fee, Late Fee, and Total Amount. For Lob
            checks the check is mailed for the Total Amount (base + fees). */}
        <Grid size={4}>
          <FormTextField
            name="transactionFee"
            label="Transaction Fee"
            control={control}
            disabled={notEditable}
          />
        </Grid>
        <Grid size={4}>
          <FormTextField
            name="lateFee"
            label="Late Fee"
            control={control}
            disabled={notEditable}
            helperText="Not charged to customer"
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="Total Amount"
            value={formatTotalAmount(watch("amount"), watch("transactionFee"), watch("lateFee"))}
            disabled
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Grid>

        {/* Customer Billing is not persisted on Lob payments (sendLobCheck
            doesn't accept billable), so we hide it for Lob to avoid
            suggesting the value will be saved. */}
        {!isLob && (
          <Grid size={12}>
            <FormSelect
              name="billable"
              label="Customer Billing"
              control={control}
              selectItems={[
                { value: true, display: "Billable to Customer" },
                {
                  value: false,
                  display: "Exclude from Customer Billing",
                },
              ]}
              disabled={billingDisabled}
            />
          </Grid>
        )}

        {/* Notes - required when billable=false OR lateFee>0. Shown for Lob too
            so a late-fee note can be captured (persisted as exceptionNote). */}
        <Grid size={12}>
          <FormTextField
            name="comment"
            label={(() => {
              const lateFeeVal = watch("lateFee");
              const lateFeeIsPresent = (safeParseFloat(String(lateFeeVal ?? 0)) ?? 0) > 0;
              const required = billable === false || lateFeeIsPresent;
              return required ? "Notes (required)" : "Notes";
            })()}
            control={control}
            disabled={billingDisabled}
            textFieldProps={{
              multiline: true,
              rows: 3,
              placeholder:
                billable === false
                  ? "Please explain why this payment is not billable..."
                  : "Add context for this payment (required when a late fee is recorded)...",
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
